import { useCallback, useEffect, useState } from 'react'
import {
  FACEBOOK_SDK_SCRIPT_ID,
  FACEBOOK_SDK_URL,
  META_EMBEDDED_SIGNUP_UNAVAILABLE_NOTIFICATION,
} from '../constants'
import type { SdkStatus } from '../types'

type UseFacebookSdkOptions = {
  appId: string
  configError: string | null
  graphApiVersion: string
  onError: (message: string) => void
}

type UseFacebookSdkResult = {
  prepareSdk: () => Promise<void>
  sdkStatus: SdkStatus
}

let facebookSdkPromise: Promise<void> | null = null
let initializedAppId: string | null = null
let initializedGraphVersion: string | null = null

function initializeFacebookSdk(appId: string, graphApiVersion: string) {
  if (!window.FB) {
    throw new Error('Facebook SDK is not available.')
  }

  if (initializedAppId === appId && initializedGraphVersion === graphApiVersion) {
    return
  }

  window.FB.init({
    appId,
    autoLogAppEvents: true,
    xfbml: true,
    version: graphApiVersion,
  })

  initializedAppId = appId
  initializedGraphVersion = graphApiVersion
}

function loadFacebookSdk(appId: string, graphApiVersion: string): Promise<void> {
  if (window.FB) {
    initializeFacebookSdk(appId, graphApiVersion)
    return Promise.resolve()
  }

  if (facebookSdkPromise) {
    return facebookSdkPromise.then(() => initializeFacebookSdk(appId, graphApiVersion))
  }

  facebookSdkPromise = new Promise((resolve, reject) => {
    let settled = false

    const resolveWhenReady = () => {
      if (settled || !window.FB) {
        return
      }

      try {
        initializeFacebookSdk(appId, graphApiVersion)
        settled = true
        resolve()
      } catch (error) {
        settled = true
        facebookSdkPromise = null
        reject(error)
      }
    }

    window.fbAsyncInit = resolveWhenReady

    const existingScript = document.getElementById(FACEBOOK_SDK_SCRIPT_ID)
    if (existingScript) {
      existingScript.addEventListener('load', resolveWhenReady, { once: true })
      existingScript.addEventListener(
        'error',
        () => {
          settled = true
          facebookSdkPromise = null
          reject(new Error('Failed to load the Facebook SDK.'))
        },
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.id = FACEBOOK_SDK_SCRIPT_ID
    script.async = true
    script.defer = true
    script.crossOrigin = 'anonymous'
    script.src = FACEBOOK_SDK_URL
    script.onload = resolveWhenReady
    script.onerror = () => {
      settled = true
      facebookSdkPromise = null
      reject(new Error('Failed to load the Facebook SDK.'))
    }

    document.body.appendChild(script)
  })

  return facebookSdkPromise
}

export function useFacebookSdk({
  appId,
  configError,
  graphApiVersion,
  onError,
}: UseFacebookSdkOptions): UseFacebookSdkResult {
  const [sdkStatus, setSdkStatus] = useState<SdkStatus>('idle')

  const prepareSdk = useCallback(async () => {
    if (configError) {
      return
    }

    setSdkStatus('loading')
    try {
      await loadFacebookSdk(appId, graphApiVersion)
      setSdkStatus('ready')
    } catch (error) {
      console.error('[MetaEmbeddedSignup] SDK load failed:', error)
      setSdkStatus('error')
      onError(META_EMBEDDED_SIGNUP_UNAVAILABLE_NOTIFICATION)
    }
  }, [appId, configError, graphApiVersion, onError])

  useEffect(() => {
    void prepareSdk()
  }, [prepareSdk])

  return {
    prepareSdk,
    sdkStatus,
  }
}
